import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { placeOrder, initiateToucanPayment, getCheckoutPaymentOptions } from '../marketApi';
import { useCart } from '../CartContext';
import { formatPrice } from '../currency';

function cartCurrency(cart) {
  const currencies = [...new Set(cart.map(i => i.currency || 'INR'))];
  return currencies.length === 1 ? currencies[0] : 'INR';
}

const COD_METHOD = {
  id: 'COD',
  icon: '💵',
  label: 'Cash on Delivery',
  desc: 'Pay in cash when your order arrives',
};

const TOUCAN_METHOD = {
  id: 'TOUCAN',
  icon: '💳',
  label: 'Pay Online',
  desc: 'UPI · Cards · Net Banking — powered by ToucanPay',
};

const FULFILLMENT_OPTIONS = [
  { id: 'PICKUP',   icon: '🏪', label: 'Pickup',        desc: 'You collect it from the seller — no courier charge' },
  { id: 'PARCEL',   icon: '📦', label: 'Parcel / To-Go', desc: 'Ready-packed for your pick-up — no courier charge' },
  { id: 'DELIVERY', icon: '🚚', label: 'Delivery',       desc: 'Delivered to your address — courier charges apply' },
];

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, cartTotal, courierTotal, grandTotal, clearCart } = useCart();
  const [toucanAvailable, setToucanAvailable] = useState(false);
  const [toucanUnavailableReason, setToucanUnavailableReason] = useState('');
  const [fulfillmentMode, setFulfillmentMode] = useState('DELIVERY');

  useEffect(() => {
    const productIds = cart.map(i => i.productId).filter(Boolean);
    if (productIds.length === 0) return;
    getCheckoutPaymentOptions(productIds)
      .then(data => {
        if (data && data.toucanAvailable) {
          setToucanAvailable(true);
          setToucanUnavailableReason('');
        } else {
          setToucanAvailable(false);
          setToucanUnavailableReason(data?.reason || '');
        }
      })
      .catch(() => {});
  }, [cart]);

  const defaultCity = cart[0]?.city || '';

  const [form, setForm] = useState({
    buyerName: '',
    phone: '',
    email: '',
    address: '',
    city: defaultCity,
    notes: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (cart.length === 0) {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: 60 }}>
        <div style={{ fontSize: 48 }}>🛒</div>
        <h2 style={{ marginTop: 12 }}>Your cart is empty</h2>
        <Link to="/" className="btn btn-primary" style={{ marginTop: 20, display: 'inline-flex' }}>
          Start Shopping
        </Link>
      </div>
    );
  }

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function validate() {
    if (!form.buyerName.trim()) return 'Please enter your name';
    if (!/^\d{10}$/.test(form.phone.trim())) return 'Please enter a valid 10-digit phone number';
    if (fulfillmentMode === 'DELIVERY' && !form.address.trim()) return 'Please enter a delivery address';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);

    const payload = {
      buyerName:       form.buyerName.trim(),
      buyerPhone:      form.phone.trim(),
      buyerAddress:    fulfillmentMode !== 'DELIVERY' ? (form.address.trim() || 'Pickup') : form.address.trim(),
      city:            form.city.trim(),
      notes:           form.notes.trim(),
      buyerEmail:      form.email.trim(),
      sellerDomain:    window.location.hostname,
      paymentMode:     paymentMethod === 'TOUCAN' ? 'ONLINE' : 'COD',
      fulfillmentMode: fulfillmentMode,
      items: cart.map((i) => ({
        productId: i.productId,
        quantity:  i.quantity,
        price:     i.price,
      })),
    };

    try {
      const result = await placeOrder(payload);
      const ref = result.orderRef || result.ref || result.id || 'UNKNOWN';

      if (paymentMethod === 'TOUCAN') {
        // Initiate ToucanPay session then redirect
        const payResult = await initiateToucanPayment({
          orderRef:   ref,
          buyerName:  form.buyerName.trim(),
          buyerPhone: form.phone.trim(),
          buyerEmail: form.email.trim(),
        });
        if (payResult && payResult.paymentUrl) {
          window.location.href = payResult.paymentUrl; // redirect first — cart cleared on return
        } else {
          setError(payResult?.error || 'Could not connect to ToucanPay. Try Cash on Delivery.');
          setLoading(false);
        }
      } else {
        clearCart();
        navigate(`/order/${ref}`, {
          state: {
            email:      form.email.trim(),
            buyerName:  form.buyerName.trim(),
            buyerPhone: form.phone.trim(),
          },
        });
      }
    } catch (ex) {
      setError(ex.message || 'Failed to place order. Please try again.');
      setLoading(false);
    }
  }

  const cur = cartCurrency(cart);
  const effectiveCourier = fulfillmentMode === 'DELIVERY' ? courierTotal : 0;
  const effectiveTotal = cartTotal + effectiveCourier;

  return (
    <div className="page-container">
      <h2 className="section-title">Checkout</h2>

      <div
        className="mk-sidebar-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr minmax(260px, 320px)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Fulfillment mode */}
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Fulfillment</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FULFILLMENT_OPTIONS.map(opt => {
                const sel = fulfillmentMode === opt.id;
                return (
                  <label key={opt.id} onClick={() => setFulfillmentMode(opt.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      border: sel ? '2px solid var(--primary)' : '2px solid var(--border)',
                      background: sel ? '#eff6ff' : 'var(--surface)', transition: 'all 0.15s',
                    }}>
                    <input type="radio" name="fulfillmentMode" value={opt.id} checked={sel}
                      onChange={() => setFulfillmentMode(opt.id)}
                      style={{ accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0 }} />
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{opt.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: sel ? 'var(--primary)' : 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{opt.desc}</div>
                    </div>
                    {sel && <span style={{ marginLeft: 'auto', color: 'var(--primary)', fontWeight: 700 }}>✓</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Delivery details */}
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18 }}>
              {fulfillmentMode === 'DELIVERY' ? 'Delivery Details' : 'Your Details'}
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-group">
              <label className="form-label">Your Name *</label>
              <input
                className="form-control"
                name="buyerName"
                value={form.buyerName}
                onChange={handleChange}
                placeholder="Full name"
                autoComplete="name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input
                className="form-control"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email{' '}
                <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional — get receipt by email)</span>
              </label>
              <input
                className="form-control"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                autoComplete="email"
              />
            </div>

            {fulfillmentMode === 'DELIVERY' && (
              <div className="form-group">
                <label className="form-label">Delivery Address *</label>
                <textarea
                  className="form-control"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="House no., street, area, landmark..."
                  rows={3}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                className="form-control"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input
                className="form-control"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Special delivery instructions..."
              />
            </div>
          </div>

          {/* Payment method selector */}
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
              Payment Method
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!toucanAvailable && toucanUnavailableReason && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, fontSize: 13,
                  background: '#fff7ed', border: '1px solid #fed7aa', color: '#92400e',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>⚠️</span>
                  <span>{toucanUnavailableReason} — only Cash on Delivery is available.</span>
                </div>
              )}
              {[COD_METHOD, ...(toucanAvailable ? [TOUCAN_METHOD] : [])].map((pm) => {
                const selected = paymentMethod === pm.id;
                return (
                  <label
                    key={pm.id}
                    onClick={() => setPaymentMethod(pm.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '14px 16px',
                      borderRadius: 10,
                      border: selected ? '2px solid #6366f1' : '2px solid var(--border)',
                      background: selected ? '#f5f3ff' : 'var(--surface)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={selected}
                      onChange={() => setPaymentMethod(pm.id)}
                      style={{ accentColor: '#6366f1', width: 18, height: 18, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 26, flexShrink: 0 }}>{pm.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: selected ? '#4f46e5' : 'var(--text)' }}>
                        {pm.label}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                        {pm.desc}
                      </div>
                    </div>
                    {selected && (
                      <span style={{ marginLeft: 'auto', color: '#6366f1', fontWeight: 700, fontSize: 18 }}>✓</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-full btn-lg"
            disabled={loading}
            style={paymentMethod === 'TOUCAN' ? { background: '#6366f1', borderColor: '#6366f1' } : {}}
          >
            {loading
              ? (paymentMethod === 'TOUCAN' ? 'Connecting to ToucanPay… (up to 10s)' : 'Placing Order…')
              : paymentMethod === 'TOUCAN'
                ? `Pay ${formatPrice(effectiveTotal, cur)} via ToucanPay →`
                : `Place Order (COD) — ${formatPrice(effectiveTotal, cur)}`}
          </button>
        </form>

        {/* Order summary */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
            Order Summary
          </div>

          {(() => {
            const groups = [];
            const seen = new Map();
            for (const item of cart) {
              const key = item.sellerName || 'Unknown Seller';
              if (!seen.has(key)) { seen.set(key, []); groups.push(key); }
              seen.get(key).push(item);
            }
            const multiSeller = groups.length > 1;
            return (
              <>
                {multiSeller && (
                  <div style={{
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                      🏪 {groups.length} sellers — separate deliveries
                    </div>
                    <div style={{ color: '#78350f', lineHeight: 1.5 }}>
                      Items from different sellers will be delivered separately.
                    </div>
                  </div>
                )}
                {groups.map((sellerName) => {
                  const items = seen.get(sellerName);
                  const sellerTotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
                  return (
                    <div key={sellerName} style={{ marginBottom: multiSeller ? 14 : 0 }}>
                      {multiSeller && (
                        <div style={{
                          fontSize: 11, fontWeight: 700, color: 'var(--primary)',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          marginBottom: 6, paddingBottom: 4,
                          borderBottom: '1px solid var(--border)',
                        }}>
                          🏪 {sellerName}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {items.map((item) => (
                          <div key={item.productId}
                            style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                            <div>
                              <div style={{ fontWeight: 600 }}>{item.name}</div>
                              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                                {formatPrice(item.price, item.currency)}/{item.unit} × {item.quantity}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700 }}>
                              {formatPrice(item.price * item.quantity, item.currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                      {multiSeller && (
                        <div style={{
                          display: 'flex', justifyContent: 'flex-end',
                          fontSize: 12, color: 'var(--muted)', marginTop: 6,
                        }}>
                          Subtotal: {formatPrice(sellerTotal, items[0]?.currency || 'INR')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}

          <hr className="divider" />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: 'var(--muted)' }}>Subtotal</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(cartTotal, cur)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
            <span style={{ color: 'var(--muted)' }}>Courier</span>
            {fulfillmentMode === 'DELIVERY' && effectiveCourier > 0
              ? <span style={{ fontWeight: 600 }}>{formatPrice(effectiveCourier, cur)}</span>
              : <span style={{ color: 'var(--success)', fontWeight: 600 }}>Free</span>
            }
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 18 }}>
            <span>Total</span>
            <span>{formatPrice(effectiveTotal, cur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
